import React, { useMemo, useCallback } from 'react';
import posthog from 'posthog-js';
import FeaturedCreatorsListView from './FeaturedCreatorsListView';
import { useStore } from '../../../../store';
import { isUserLoggedIn } from '../../../../utils/analytics';
import { parseFeaturedCollectionMetafields } from '../../../../utils/collections';
import config from '../../../../../config.json';

const { routes } = config;

const EXCLUDED_COLLECTION_IDS = ['310093906076']; // Forsaken collection
const MAX_FEATURED_COLLECTIONS = 16;

type FeaturedCreatorsListContainerProps = {
  featuredCollections: CollectionEdgeResponse[] | null;
};

type ParsedCreator = {
  id: string;
  handle: string;
  title: string;
  imageSrc: string;
};

const FeaturedCreatorsListContainer: React.FC<FeaturedCreatorsListContainerProps> = ({ featuredCollections }) => {
  const { state } = useStore();

  const processedCreators = useMemo(() => {
    if (!featuredCollections) return null;

    const filteredCollections = featuredCollections.filter(
      (collection) => !EXCLUDED_COLLECTION_IDS.some((id) => collection.node?.id.includes(id)),
    );

    const slicedCollections = filteredCollections.slice(0, MAX_FEATURED_COLLECTIONS);

    return slicedCollections.map((collection): ParsedCreator => {
      const { node } = collection;
      const { metafields } = node;
      const { creatorLogoURL, creatorAvatarURL } = parseFeaturedCollectionMetafields(metafields.edges);
      const creatorLogoSrc = creatorLogoURL || creatorAvatarURL;

      return {
        id: node.id,
        handle: node.handle || '',
        title: node.title || '',
        imageSrc: creatorLogoSrc || '',
      };
    });
  }, [featuredCollections]);

  const handleViewAllClick = useCallback(() => {
    posthog.capture('homepage_productList_viewAll_click', {
      link_href: routes.featuredCreators,
      link_title: 'View All',
      is_logged_in: isUserLoggedIn(state.user),
      section_title: 'Featured Creators',
    });
  }, [state.user]);

  if (!processedCreators || processedCreators.length === 0) {
    return null;
  }

  return <FeaturedCreatorsListView creators={processedCreators} onViewAllClick={handleViewAllClick} />;
};

export default FeaturedCreatorsListContainer;
